/** React glue around FieldApp: language, session, combined sync status, store queries. */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { Emitter, Topic } from "@core/emitter";
import type { CombinedSyncStatus, FieldApp } from "../field/app";
import type { FieldUser } from "../field/api";
import { STRINGS, type Lang, type Strings } from "../i18n";
import { prefs } from "../platform/browser";

interface Ctx {
  app: FieldApp;
  lang: Lang;
  t: Strings;
  toggleLang: () => void;
}

const AppCtx = createContext<Ctx | null>(null);

export function AppProvider({ app, children }: { app: FieldApp; children: React.ReactNode }) {
  const [lang, setLang] = useState<Lang>(prefs.get("lang") === "en" ? "en" : "hi");
  const toggleLang = useCallback(() => {
    setLang((l) => {
      const n = l === "hi" ? "en" : "hi";
      prefs.set("lang", n);
      return n;
    });
  }, []);
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);
  const value = useMemo(() => ({ app, lang, t: STRINGS[lang], toggleLang }), [app, lang, toggleLang]);
  return <AppCtx.Provider value={value}>{children}</AppCtx.Provider>;
}

export function useApp(): Ctx {
  const c = useContext(AppCtx);
  if (!c) throw new Error("useApp outside AppProvider");
  return c;
}

/** re-renders on every change; returns a counter for effects that must re-run on a change */
export function useEmitter(em: Emitter): number {
  const [n, setN] = useState(0);
  useEffect(() => em.subscribe(() => setN((x) => x + 1)), [em]);
  return n;
}

export function useSession(): { user: FieldUser | null; token: string | null; assemblyId: string | null } {
  const { app } = useApp();
  useEmitter(app.core.changes);
  return { user: app.user, token: app.token, assemblyId: app.assemblyId };
}

export function useSyncStatus(): CombinedSyncStatus {
  const { app } = useApp();
  useEmitter(app.statusChanges);
  return app.status();
}

const DEFAULT_TOPICS: Topic[] = ["booths", "sections", "outbox"];

/**
 * Load from the local database; load again when the stores change. Draft autosaves only reload
 * queries that ask for "drafts", so typing never re-renders the booth list.
 */
export function useQuery<T>(load: () => Promise<T>, deps: React.DependencyList, topics: Topic[] = DEFAULT_TOPICS): T | undefined {
  const { app } = useApp();
  const [data, setData] = useState<T | undefined>(undefined);
  const loadRef = useRef(load);
  loadRef.current = load;
  const seq = useRef(0);
  const topicKey = topics.join(",");

  useEffect(() => {
    const wanted = new Set(topicKey.split(","));
    let queued = false;
    const reload = () => {
      const my = ++seq.current;
      loadRef
        .current()
        .then((d) => {
          if (my === seq.current) setData(d);
        })
        .catch(() => undefined);
    };
    const onChange = (topic: Topic) => {
      if (!wanted.has(topic) || queued) return;
      queued = true;
      void Promise.resolve().then(() => {
        queued = false;
        reload();
      });
    };
    reload();
    const a = app.core.store.changes.subscribe(onChange);
    const b = app.polling.changes.subscribe(onChange);
    return () => {
      a();
      b();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app, topicKey, ...deps]);

  return data;
}
